// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import React, { useMemo } from 'react';
import { type MontecarloSimulationResp } from '@shared/data-access/interfaces/api/ai/montecarlo';
import {
  simulationEndingBalanceRow,
  simulationTableRows,
} from '../utils/simulation-table-rows';
import { PopupButton } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';
import { type PopupsName } from '@shared/ui-kit/components';
interface SimulationTableProps {
  simulation: MontecarloSimulationResp[];
}
export interface SimulationTableRow {
  label: string;
  popup?: PopupsName;
  percentile_10th: string;
  percentile_25th: string;
  percentile_50th: string;
  percentile_75th: string;
  percentile_90th: string;
}
export const SimulationTable = ({ simulation }: SimulationTableProps) => {
  const { t } = useTranslation('common');
  const simulationData: SimulationTableRow[] = useMemo(
    () => [
      simulationTableRows(simulation, 'average_returns', t),
      simulationTableRows(simulation, 'volatility', t),
      simulationTableRows(simulation, 'sharpe', t),
      simulationTableRows(simulation, 'sortino', t),
      simulationTableRows(simulation, 'max_drawdawns', t),
      simulationEndingBalanceRow(simulation, t),
    ],
    [simulation, t],
  );

  return (
    <table className="w-full font-[poppins] text-primary-medium">
      <thead>
        <tr>
          <th></th>

          <th className="p-3 text-right font-normal leading-[24px] text-border-dark">
            {t('common.percentile-10')}
          </th>

          <th className="p-3 text-right font-normal leading-[24px] text-border-dark">
            {t('common.percentile-25')}
          </th>

          <th className="rounded-t-lg bg-info-blue p-3 text-right font-semibold leading-[24px] text-primary">
            {t('common.percentile-50')}
          </th>

          <th className="p-3 text-right font-normal leading-[24px] text-border-dark">
            {t('common.percentile-75')}
          </th>

          <th className="p-3 text-right font-normal leading-[24px] text-border-dark">
            {t('common.percentile-90')}
          </th>
        </tr>
      </thead>
      <tbody>
        {simulationData.map((sim: SimulationTableRow, idx: number) => (
          <React.Fragment key={sim.label}>
            <tr
              key={sim.label}
              className="last:!rounded-lg last:bg-info-blue last:font-semibold last:!text-primary"
            >
              <td className="rounded-l-lg p-3">
                <div className="flex">
                  <p>{sim.label}</p>
                  {sim?.popup && <PopupButton popupName={sim.popup} tooltip />}
                </div>
              </td>
              <td className="!money-display p-3 text-right">
                {sim.percentile_10th || '-'}
              </td>
              <td className="!money-display p-3 text-right">
                {sim.percentile_25th || '-'}
              </td>
              <td
                className={`!money-display p-3 text-right font-semibold text-primary ${
                  idx === simulationData.length - 1
                    ? 'bg-transparent'
                    : 'bg-info-blue'
                }`}
              >
                {sim.percentile_50th || '-'}
              </td>
              <td className="!money-display p-3 text-right">
                {sim.percentile_75th || '-'}
              </td>
              <td className="!money-display rounded-r-lg p-3 text-right">
                {sim.percentile_90th || '-'}
              </td>
            </tr>
            {idx === simulationData.length - 2 && (
              <tr>
                <td colSpan={6}>
                  <hr className="h-px border-none bg-sidebar-text" />
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};
